import { NgModule } from '@angular/core';
import { Routes, RouterModule, NoPreloading } from '@angular/router';
import { ErrorPageComponent } from '@app/layouts/components/error-page.component';
import { Error404Component } from '@app/layouts/components/error-404.component';
import { HomePageComponent } from '@app/layouts/components/home-page.component';
import { LayoutComponent } from '@app/layouts/layouts';
import { SeoGuard } from '@app/core/guards/seo-guard.service';
import { PageResolver } from '@app/core/services/page-resolver.service';
import { BookmakersPromoRedirector } from '@app/core/guards/bookmakers-promo.redirector';
import {
    customPageMatcher,
    сontestRulesMatcher,
    getSubPostMatcher,
    blogMatcher,
    blogCreateMatcher,
    profile2IndexMatcher,
    bookmakersPromoMatcher,
} from './url.matchers';

const routes: Routes = [
    {
        path: '',
        component: LayoutComponent,
        children: [
            {
                path: '',
                component: HomePageComponent,
                data: {
                    taxonomy: 'home'
                },
                canActivate: [
                    SeoGuard,
                ],
                resolve: {
                    pageConfig: PageResolver,
                }
            },
            {
                matcher: bookmakersPromoMatcher,
                component: ErrorPageComponent,
                canActivate: [
                    BookmakersPromoRedirector,
                ],
                data: {
                    '@start': 11, // /bookmakers/...
                    '@prefix': '',
                },
            },
            {
                matcher: getSubPostMatcher(['prosport']),
                loadChildren: () => import('./prosport/prosport.module').then((m) => m.ProsportModule),
            },
            {
                matcher: getSubPostMatcher(['promoAndBonuses'], ['create', 'update']),
                loadChildren: () => import('./bookmakers-promo-create/bookmakers-promo-create.module').then((m) => m.BookmakersPromoCreateModule),
            },

            {
                matcher: blogCreateMatcher,
                loadChildren: () => import('./blog-create/blog-create.module').then(m => m.BlogCreateModule),
            },
            {
                matcher: сontestRulesMatcher,
                loadChildren: () => import('./contest-rules/contest-rules.module').then(m => m.ContestRulesModule),
            },

            {
                matcher: customPageMatcher,
                loadChildren: () => import('./page/page.module').then(m => m.PageModule),
            },

            {
                matcher: getSubPostMatcher(['ovbPromo', 'selectionPromo', 'promoAndBonuses']),
                loadChildren: () => import('./bookmakers-promo/bookmakers-promo.module').then((m) => m.BookmakersPromoModule),
            },
            {
                matcher: blogMatcher,
                loadChildren: () => import('./blog/blog.module').then(m => m.BlogModule),
            },
            {
                path: 'error',
                canActivate: [
                    SeoGuard,
                ],
                children: [
                    {
                        path: '**',
                        component: ErrorPageComponent
                    }
                ]
            },
            {
                path: 'copyright',
                loadChildren: () => import('./copyright/copyright.module').then(m => m.CopyrightModule),
            },
            {
                path: 'chat',
                loadChildren: () => import('./chat/chat.module').then(m => m.ChatModule),
            },
            {
                path: 'about',
                loadChildren: () => import('./about/about.module').then(m => m.AboutModule),
            },
            {
                path: 'loyalty',
                loadChildren: () => import('./loyalty2/loyalty.module').then(m => m.LoyaltyModule),
            },
            {
                path: 'tips/contest/my',
                loadChildren: () => import('./contest-my/contest-my.module').then(m => m.ContestMyModule),
            },
            {
                path: 'tips/contest',
                loadChildren: () => import('./contest6/contest6.module').then(m => m.Contest6Module),
            },

            {
                path: 'faq',
                loadChildren: () => import('./faq/faq.module').then(m => m.FaqModule),
            },
            {
                path: 'tips/coupon',
                loadChildren: () => import('./coupon6/coupon6.module').then(m => m.Coupon6Module),
            },
            {
                path: 'tips/rating',
                loadChildren: () => import('./kappers-rating/kappers-rating.module').then(m => m.KappersRatingModule),
            },
            {
                path: 'tips/my/votes',
                redirectTo: '/tips/my',
                pathMatch: 'full',
            },
            {
                path: 'tips',
                loadChildren: () => import('./tips6/tips.module').then(m => m.TipsModule),
            },
            {
                path: 'odds',
                loadChildren: () => import('./odds6/odds2.module').then(m => m.Odds2Module),
            },
            {
                path: 'matches',
                loadChildren: () => import('./matches/matches.module').then(m => m.MatchesModule),
            },
            {
                path: 'streams',
                loadChildren: () => import('./matches/streams.module').then(m => m.StreamsModule),
            },
            {
                path: 'contacts',
                loadChildren: () => import('./contacts/contacts.module').then(m => m.ContactsModule),
            },
            {
                path: 'user/profile/notifications',
                loadChildren: () => import('./notification/notification.module').then(m => m.NotificationModule),
            },
            {
                matcher: profile2IndexMatcher,
                loadChildren: () => import('./profile2/profile2.module').then(m => m.Profile2Module),
            },
            {
                path: 'user/profile/manage',
                loadChildren: () => import('./profile2-manage/profile2-manage.module').then(m => m.Profile2ManageModule),
            },
            {
                path: 'user',
                loadChildren: () => import('./auth/auth.module').then(m => m.AuthModule),
            },
            {
                path: 'bookmakers/reviews',
                loadChildren: () => import('./bookmakers-comments/bookmakers-comments.module').then(m => m.BookmakersCommentsModule),
            },
            {
                path: 'learning',
                loadChildren: () => import('@app/learning/learning.module').then(m => m.LearningModule),
            },
            {
                path: 'bookmakers/hints',
                loadChildren: () => import('./bookmakers-hints/bookmakers-hints.module').then(m => m.BookmakersHintsModule),
            },
            {
                path: 'bookmakers/apps',
                loadChildren: () => import('./bookmakers-apps/bookmakers-apps.module').then(m => m.BookmakersAppsModule),
            },
            {
                path: 'bookmakers',
                loadChildren: () => import('@app/bookmakers/bookmakers.module').then(m => m.BookmakersModule),
            },
            {
                path: 'bookmakers',
                loadChildren: () => import('./bookmakers-pages/bookmakers-pages.module').then(m => m.BookmakersPagesModule),
            },
            {
                path: '**',
                component: Error404Component,
                data: {
                    code: '404'
                },
                canActivate: [
                    SeoGuard,
                ]
          }

        ],
    },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    preloadingStrategy: NoPreloading,
    scrollPositionRestoration: 'top',
    initialNavigation: 'enabledNonBlocking'
})],
  exports: [RouterModule]
})
export class AppRoutingModule {}
